import { useState } from "react";
import { toast } from "sonner";
import { Truck, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, eur } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export function CitySelect({ value, onChange, testid, placeholder = "Ville" }) {
  const { meta } = useMe();
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {meta.cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} ({c.postal_code})</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const SLOTS = ["Dès que possible", "Aujourd'hui 14h–18h", "Demain 9h–12h", "Demain 14h–18h"];

export function DeliveryQuoteForm({ listing, onSelect, selectedId }) {
  const api = useApi();
  const [f, setF] = useState({
    pickup_city: listing?.city || "", dropoff_city: "", weight_kg: listing?.weight_kg || 5,
    length_cm: 50, width_cm: 40, height_cm: 40, declared_value: listing?.price || 50, slot: SLOTS[0],
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const run = async () => {
    setBusy(true);
    try {
      const body = { ...f, listing_id: listing?.id, weight_kg: +f.weight_kg, length_cm: +f.length_cm, width_cm: +f.width_cm, height_cm: +f.height_cm, declared_value: +f.declared_value };
      const { data } = await api.post("/deliveries/quote", body);
      setResult(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="delivery-quote-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Collecte</Label><CitySelect value={f.pickup_city} onChange={set("pickup_city")} testid="quote-pickup-select" /></div>
        <div><Label>Destination</Label><CitySelect value={f.dropoff_city} onChange={set("dropoff_city")} testid="quote-dropoff-select" /></div>
        <div className="grid grid-cols-3 gap-2 sm:col-span-2">
          {[["length_cm", "Long. (cm)"], ["width_cm", "Larg. (cm)"], ["height_cm", "Haut. (cm)"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input data-testid={`quote-${k}`} type="number" value={f[k]} onChange={set(k)} className="bg-white" /></div>
          ))}
        </div>
        <div><Label>Poids (kg)</Label><Input data-testid="quote-weight" type="number" value={f.weight_kg} onChange={set("weight_kg")} className="bg-white" /></div>
        <div><Label>Valeur déclarée (€)</Label><Input data-testid="quote-value" type="number" value={f.declared_value} onChange={set("declared_value")} className="bg-white" /></div>
        <div className="sm:col-span-2">
          <Label>Créneau</Label>
          <Select value={f.slot} onValueChange={set("slot")}>
            <SelectTrigger data-testid="quote-slot-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button data-testid="quote-submit-btn" disabled={busy || !f.pickup_city || !f.dropoff_city} onClick={run} className="rounded-full bg-forest hover:bg-forest/90">
        <Truck className="w-4 h-4 mr-2" /> {busy ? "Estimation…" : "Vérifier l'éligibilité et estimer"}
      </Button>
      {result && !result.eligible && (
        <div data-testid="quote-ineligible" className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Livraison non éligible</p>
          <ul className="list-disc ml-5 mt-2">{result.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
      )}
      {result?.eligible && (
        <div className="grid gap-3" data-testid="quote-results">
          {result.quotes.map((q) => (
            <button key={q.id} type="button" data-testid={`quote-option-${q.provider}`} onClick={() => onSelect?.(q)}
              className={`text-left rounded-xl border p-4 flex items-center justify-between transition-colors ${selectedId === q.id ? "border-forest bg-forest/5" : "border-stone-200 bg-white hover:border-forest"}`}>
              <div>
                <p className="font-medium">{q.provider_label}</p>
                <p className="text-xs text-stone-500 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> {q.eta_min >= 1440 ? `~${Math.round(q.eta_min / 1440)} jour(s)` : `~${q.eta_min} min`} · {q.distance_km} km · devis valable 30 min</p>
                {q.relay_point && <p className="text-xs text-terra mt-1" data-testid={`quote-relay-${q.provider}`}>Retrait : {q.relay_point}</p>}
              </div>
              <p className="font-serif text-2xl text-forest">{eur(q.client_price_ttc)}</p>
            </button>
          ))}
          <p className="text-xs text-stone-500">Tarifs TTC fictifs (Uber Direct, coursier local, Mondial Relay, Chronopost simulés). Aucune collecte réelle n'est garantie tant qu'aucun accord transporteur n'est signé.</p>
        </div>
      )}
    </div>
  );
}
